import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import CommonFormDropDown from "../formdropdown";
import { DataTable, PaginationBar } from "./tableKit";
import { ClaimStateChip } from "./StatusChip";
import ClaimDetailDrawer from "./ClaimDetailDrawer";
import ConfirmDialog from "./ConfirmDialog";
import Api from "../../containers/dashboard/api";
import {
  formatCurrency,
  formatDateTime,
  CLAIM_STATE_OPTIONS,
  PAGE_SIZE,
  toMessages,
} from "./utils";
import {
  getBonusClaimListAction,
  getBonusClaimListErrorAction,
  getBonusClaimInfoAction,
  getBonusClaimInfoErrorAction,
  deleteBonusClaimAction,
  deleteBonusClaimSuccessAction,
  deleteBonusClaimErrorAction,
} from "../../containers/dashboard/action";

const useStyles = makeStyles(() => ({
  toolbar: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
    alignItems: "flex-end",
    marginBottom: "20px",
  },
  filterField: { width: "210px" },
  filterLabel: {
    fontSize: "13px",
    color: theme.colors.Violet,
    marginBottom: "5px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    backgroundColor: theme.colors.Zumthor,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "4px",
    outline: "none",
  },
  actionsCell: { display: "flex", gap: "4px", justifyContent: "flex-end" },
}));

const ALL_BONUSES = { value: "", label: "All bonuses" };

function ClaimsTable() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // Filters
  const [bonus, setBonus] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [employee, setEmployee] = useState("");
  const [employeeInput, setEmployeeInput] = useState("");
  const [page, setPage] = useState(1);

  // Bonus filter options (fetched locally so it doesn't disturb the bonuses tab's list).
  const [bonusOptions, setBonusOptions] = useState([ALL_BONUSES]);

  // Drawer + dialog
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Redux state
  const claimList = useSelector((s) => s.dashboardReducer?.bonusClaimList);
  const loading = useSelector((s) => !!s.dashboardReducer?.bonusClaimListLoading);
  const listError = useSelector((s) => s.dashboardReducer?.bonusClaimListError);
  const claimInfo = useSelector((s) => s.dashboardReducer?.bonusClaimInfo);
  const claimInfoLoading = useSelector(
    (s) => !!s.dashboardReducer?.bonusClaimInfoLoading
  );
  const claimInfoError = useSelector((s) => s.dashboardReducer?.bonusClaimInfoError);
  const deleteLoading = useSelector(
    (s) => !!s.dashboardReducer?.deleteBonusClaimLoading
  );
  const deleteSuccess = useSelector((s) => s.dashboardReducer?.deleteBonusClaimSuccess);
  const deleteError = useSelector((s) => s.dashboardReducer?.deleteBonusClaimError);

  const items = claimList?.data || [];
  const count = claimList?.count || 0;

  const buildFilters = useCallback(
    (overrides = {}) => ({
      bonus: bonus || undefined,
      employee: employee || undefined,
      state: stateFilter || undefined,
      page,
      page_size: PAGE_SIZE,
      ...overrides,
    }),
    [bonus, employee, stateFilter, page]
  );

  const refetch = useCallback(
    (overrides) => dispatch(getBonusClaimListAction(buildFilters(overrides))),
    [dispatch, buildFilters]
  );

  useEffect(() => {
    dispatch(getBonusClaimListAction(buildFilters()));
  }, [dispatch, buildFilters]);

  // Populate the bonus filter dropdown once (local fetch, outside redux list state).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await Api.getBonusList({ page_size: 100 });
        if (!active || !res || res.message) return;
        const opts = (res.data || []).map((b) => ({
          value: b.id,
          label: b.name,
        }));
        setBonusOptions([ALL_BONUSES, ...opts]);
      } catch (e) {
        // Non-fatal: the dropdown just stays at "All bonuses".
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  /* ----------------------- notistack + state resets ---------------------- */
  useEffect(() => {
    if (!listError) return;
    toMessages(listError, "Unable to load claims.").forEach((msg) =>
      enqueueSnackbar(msg, {
        variant: "error",
        onClose: () => dispatch(getBonusClaimListErrorAction(null)),
      })
    );
    dispatch(getBonusClaimListErrorAction(null));
  }, [listError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!claimInfoError) return;
    toMessages(claimInfoError, "Unable to load claim.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(getBonusClaimInfoErrorAction(null));
  }, [claimInfoError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!deleteSuccess) return;
    enqueueSnackbar("Claim deleted.", { variant: "success" });
    dispatch(deleteBonusClaimSuccessAction(null));
    setDeleteTarget(null);
    setDrawerOpen(false);
    setSelectedClaim(null);
    const remaining = items.length - 1;
    const nextPage = remaining <= 0 && page > 1 ? page - 1 : page;
    if (nextPage !== page) setPage(nextPage);
    else refetch();
  }, [deleteSuccess, enqueueSnackbar, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deleteError) return;
    toMessages(deleteError, "Unable to delete claim.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(deleteBonusClaimErrorAction(null));
    setDeleteTarget(null);
  }, [deleteError, enqueueSnackbar, dispatch]);

  /* ------------------------------ handlers ------------------------------- */
  const openDetail = (claim) => {
    setSelectedClaim(claim);
    setDrawerOpen(true);
    dispatch(getBonusClaimInfoAction(claim.id)); // refresh with latest payment/state
  };

  const commitEmployeeFilter = () => {
    if (employeeInput.trim() !== employee) {
      setPage(1);
      setEmployee(employeeInput.trim());
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "employee_name",
        header: "Employee",
        skeletonWidth: "80%",
        render: (r) => r.employee_name || "—",
      },
      { key: "bonus_name", header: "Bonus", render: (r) => r.bonus_name || "—" },
      { key: "amount", header: "Amount", render: (r) => formatCurrency(r.amount) },
      {
        key: "state",
        header: "Status",
        render: (r) => <ClaimStateChip state={r.state} />,
      },
      {
        key: "redeemed_at",
        header: "Redeemed",
        render: (r) => formatDateTime(r.redeemed_at),
      },
      {
        key: "created_at",
        header: "Created",
        render: (r) => formatDateTime(r.created_at),
      },
      {
        key: "actions",
        header: "",
        align: "right",
        stopPropagation: true,
        render: (r) => (
          <div className={classes.actionsCell}>
            <Tooltip title="View">
              <IconButton
                size="small"
                aria-label="View claim"
                onClick={() => openDetail(r)}
              >
                <VisibilityOutlinedIcon
                  fontSize="small"
                  style={{ color: theme.colors.DodgerBlue }}
                />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Delete">
              <IconButton
                size="small"
                aria-label="Delete claim"
                onClick={() => setDeleteTarget(r)}
              >
                <DeleteOutlineIcon
                  fontSize="small"
                  style={{ color: theme.colors.RejectRed }}
                />
              </IconButton>
            </Tooltip> */}
          </div>
        ),
      },
    ],
    [classes.actionsCell] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      <div className={classes.toolbar}>
        <div className={classes.filterField}>
          <div className={classes.filterLabel}>Bonus</div>
          <CommonFormDropDown
            placeholder="All bonuses"
            options={bonusOptions}
            value={bonusOptions.find((o) => o.value === bonus) || ALL_BONUSES}
            onChange={(opt) => {
              setPage(1);
              setBonus(opt?.value || "");
            }}
          />
        </div>
        <div className={classes.filterField}>
          <div className={classes.filterLabel}>Status</div>
          <CommonFormDropDown
            placeholder="All states"
            options={CLAIM_STATE_OPTIONS}
            value={
              CLAIM_STATE_OPTIONS.find((o) => o.value === stateFilter) ||
              CLAIM_STATE_OPTIONS[0]
            }
            onChange={(opt) => {
              setPage(1);
              setStateFilter(opt?.value || "");
            }}
          />
        </div>
        <div className={classes.filterField}>
          <div className={classes.filterLabel}>Employee ID</div>
          <input
            className={classes.searchInput}
            placeholder="Filter by employee ID"
            value={employeeInput}
            onChange={(e) => setEmployeeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEmployeeFilter();
            }}
            onBlur={commitEmployeeFilter}
            aria-label="Filter by employee ID"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        skeletonRows={PAGE_SIZE}
        emptyTitle="No claims found"
        emptyHint="Claims appear here once employees redeem a bonus."
        onRowClick={openDetail}
      />

      <PaginationBar
        count={count}
        page={page}
        next={claimList?.next}
        previous={claimList?.previous}
        pageSize={PAGE_SIZE}
        onChange={(value) => setPage(value)}
      />

      <ClaimDetailDrawer
        open={drawerOpen}
        claim={claimInfo || selectedClaim}
        loading={claimInfoLoading}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedClaim(null);
        }}
        onDelete={(claim) => setDeleteTarget(claim)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete claim?"
        description={
          deleteTarget
            ? `This claim for ${deleteTarget.employee_name || "the employee"} will be permanently removed.`
            : ""
        }
        confirmText="Delete"
        loading={deleteLoading}
        onConfirm={() =>
          deleteTarget && dispatch(deleteBonusClaimAction(deleteTarget.id))
        }
        onClose={() => {
          if (deleteLoading) return;
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}

export default ClaimsTable;
